/**
 * Fan automation system with weather-based presets
 * Matches the original script.js automation logic
 */

import { WeatherCondition, computeWeatherCondition } from './weather';

export type FanMode = 'very_hot' | 'hot' | 'always_on';

export interface FanModePreset {
  description: string;
  turnOnConditions: Array<WeatherCondition | "always">;
  turnOffConditions: WeatherCondition[];
}

export const fanModePresets: Record<FanMode, FanModePreset> = {
  very_hot: {
    description: 'Fan turns on when weather is Hot & Dry or Sunny',
    turnOnConditions: ['hot', 'sunny'],
    turnOffConditions: ['cold'],
  },
  hot: {
    description: 'Fan turns on when weather is Hot & Dry, Sunny, or Partly Cloudy',
    turnOnConditions: ['hot', 'sunny', 'partly-cloudy'],
    turnOffConditions: ['cold'],
  },
  always_on: {
    description: 'Fan stays on regardless of weather conditions',
    turnOnConditions: ['always'],
    turnOffConditions: [],
  },
};

export interface AutomationState {
  enabled: boolean;
  mode: FanMode;
  fanAutoState: boolean;
  conditionMetSince: number | null;
  activationTimer: NodeJS.Timeout | null;
}

export const checkAutomation = (
  state: AutomationState,
  weatherData: {
    temperature: number | null;
    humidity: number | null;
    pressure?: number | null;
    light?: number | null;
    rain?: number | null;
    windSpeed?: number | null;
  },
  onFanCommand: (command: 'ON' | 'OFF', isAuto: boolean) => void
): AutomationState => {
  if (!state.enabled) {
    return state;
  }

  const preset = fanModePresets[state.mode];
  if (!preset) {
    return state;
  }

  // Always ON mode
  if (state.mode === 'always_on') {
    if (!state.fanAutoState) {
      const delay = 1000; // 1 second delay for noise filtering
      if (state.conditionMetSince === null) {
        const newConditionMetSince = Date.now();
        const timer = setTimeout(() => {
          if (!state.fanAutoState && state.enabled) {
            onFanCommand('ON', true);
          }
        }, delay);
        return {
          ...state,
          conditionMetSince: newConditionMetSince,
          activationTimer: timer,
        };
      }
    }
    return state;
  }

  // For other modes, require sensor data
  if (weatherData.temperature === null || weatherData.humidity === null) {
    return state;
  }

  // Get current weather condition
  const weatherCondition = computeWeatherCondition(
    weatherData.temperature,
    weatherData.humidity,
    weatherData.pressure ?? null,
    weatherData.light ?? null,
    weatherData.rain ?? null,
    weatherData.windSpeed ?? null
  );

  // Check if weather condition should turn fan ON
  const shouldTurnOn =
    preset.turnOnConditions.includes(weatherCondition) ||
    preset.turnOnConditions.includes('always');

  // Check if weather condition should turn fan OFF
  const shouldTurnOff = preset.turnOffConditions.includes(weatherCondition);

  // Turn ON logic with delay
  if (shouldTurnOn && !state.fanAutoState) {
    if (state.conditionMetSince === null) {
      const delay = 2000; // 2 second delay for weather condition stability
      const newConditionMetSince = Date.now();
      const timer = setTimeout(() => {
        // Re-check weather condition
        const currentWeather = computeWeatherCondition(
          weatherData.temperature!,
          weatherData.humidity!,
          weatherData.pressure ?? null,
          weatherData.light ?? null,
          weatherData.rain ?? null,
          weatherData.windSpeed ?? null
        );
        const stillOn =
          preset.turnOnConditions.includes(currentWeather) ||
          preset.turnOnConditions.includes('always');
        const notOff = !preset.turnOffConditions.includes(currentWeather);

        if (stillOn && notOff && !state.fanAutoState && state.enabled) {
          onFanCommand('ON', true);
        }
      }, delay);

      return {
        ...state,
        conditionMetSince: newConditionMetSince,
        activationTimer: timer,
      };
    }
  } else if (shouldTurnOff && state.fanAutoState) {
    // Turn OFF immediately
    if (state.activationTimer) {
      clearTimeout(state.activationTimer);
    }
    onFanCommand('OFF', true);
    return {
      ...state,
      conditionMetSince: null,
      activationTimer: null,
      fanAutoState: false,
    };
  } else if (!shouldTurnOn && state.conditionMetSince !== null) {
    // Cancel timer if condition no longer met
    if (state.activationTimer) {
      clearTimeout(state.activationTimer);
    }
    return {
      ...state,
      conditionMetSince: null,
      activationTimer: null,
    };
  }

  return state;
};

